/* global axios */
import ApiClient from './ApiClient';
import { resourcePath, requestConfig, withQuery } from './lais/httpHelpers';

const attachmentList = payload => payload.attachments || payload.files || [];

const withoutAttachments = payload =>
  Object.entries(payload).reduce((acc, [key, value]) => {
    if (['attachments', 'files'].includes(key)) return acc;
    if (value === undefined || value === null || value === '') return acc;
    acc[key] = value;
    return acc;
  }, {});

const appendObject = (formData, namespace, payload = {}) => {
  Object.entries(withoutAttachments(payload)).forEach(([key, value]) => {
    formData.append(namespace ? `${namespace}[${key}]` : key, value);
  });
};

const appendAttachments = (formData, payload = {}) => {
  attachmentList(payload).forEach(file => {
    formData.append('attachments[]', file);
  });
};

const buildCreatePayload = payload => {
  if (!attachmentList(payload).length) {
    return { support_ticket: withoutAttachments(payload) };
  }

  const formData = new FormData();
  appendObject(formData, 'support_ticket', payload);
  appendAttachments(formData, payload);
  return formData;
};

const buildReplyPayload = payload => {
  if (!attachmentList(payload).length) return withoutAttachments(payload);

  const formData = new FormData();
  appendObject(formData, '', payload);
  appendAttachments(formData, payload);
  return formData;
};

class SupportTicketPortalAPI extends ApiClient {
  constructor() {
    super('support_tickets/portal', { accountScoped: true });
  }

  list(params = {}) {
    return axios.get(withQuery(this.url, params), requestConfig());
  }

  show(id) {
    return axios.get(resourcePath(this.url, id), requestConfig());
  }

  create(payload = {}) {
    return axios.post(this.url, buildCreatePayload(payload), requestConfig());
  }

  createReply(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, 'replies'),
      buildReplyPayload(payload),
      requestConfig()
    );
  }

  // SPEC 43 / P37-21 — triagem administrativa de ocorrências por SLA/prioridade.
  triage(params = {}) {
    return axios.get(withQuery(`${this.url}/triage`, params), requestConfig());
  }

  assign(id, assigneeId) {
    return axios.post(
      resourcePath(this.url, id, 'assign'),
      { assignee_id: assigneeId },
      requestConfig()
    );
  }

  handover(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, 'handover'),
      payload,
      requestConfig()
    );
  }

  updatePriority(id, priority) {
    return axios.post(
      resourcePath(this.url, id, 'priority'),
      { priority },
      requestConfig()
    );
  }

  assistanceReport(params = {}) {
    return axios.get(
      withQuery(resourcePath(this.url, 'assistance', 'report'), params),
      requestConfig()
    );
  }

  createAssistance(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, 'assistance'),
      { assistance: payload },
      requestConfig()
    );
  }

  reopenAssistance(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, 'assistance', 'reopen'),
      { assistance: payload },
      requestConfig()
    );
  }

  approveAssistanceBudget(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, 'assistance', 'approve_budget'),
      { assistance: payload },
      requestConfig()
    );
  }

  recordAssistanceCost(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, 'assistance', 'record_cost'),
      { assistance: payload },
      requestConfig()
    );
  }

  recordAssistanceEvidence(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, 'assistance', 'evidence'),
      { assistance: payload },
      requestConfig()
    );
  }

  recordAssistanceOutcome(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, 'assistance', 'outcome'),
      { assistance: payload },
      requestConfig()
    );
  }
}

export default new SupportTicketPortalAPI();
